import { useState, useEffect } from "react";
import { BookOpen } from "lucide-react";
import { collections, findItemBySlugAndId } from "./data/collections";
import type { StoryItem, Collection } from "./data/types";
import { ItemCard } from "./components/ItemCard";
import { DetailPage } from "./components/DetailPage";
import "./index.css";

interface RouteMatch {
  collection: Collection;
  item: StoryItem;
}

function getRoute(): RouteMatch | null {
  const path = window.location.pathname;
  for (const col of collections) {
    const prefix = `/${col.slug}/`;
    if (path.startsWith(prefix)) {
      const id = path.slice(prefix.length);
      const match = findItemBySlugAndId(col.slug, id);
      if (match) return match;
    }
  }
  return null;
}

export function App() {
  const [selected, setSelected] = useState<RouteMatch | null>(getRoute);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const onPopState = () => setSelected(getRoute());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  function navigate(collection: Collection, item: StoryItem) {
    window.history.pushState(null, "", `/${collection.slug}/${item.id}`);
    setSelected({ collection, item });
    window.scrollTo(0, 0);
  }

  function goHome() {
    window.history.pushState(null, "", "/");
    setSelected(null);
    window.scrollTo(0, 0);
  }

  if (selected) {
    return (
      <DetailPage
        collection={selected.collection}
        item={selected.item}
        onBack={goHome}
        onNavigate={(col, item) => navigate(col, item)}
      />
    );
  }

  const col = collections[activeTab];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-5 flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center shrink-0">
            <BookOpen size={22} className="text-white" />
          </div>
          <div>
            <h1 style={{ fontFamily: "var(--font-heading)" }} className="text-lg font-bold text-gray-900 leading-tight">
              Stories
            </h1>
            <p className="text-xs text-gray-500 mt-0.5" style={{ fontFamily: "var(--font-body)" }}>
              Cerita pendek untuk belajar dan refleksi
            </p>
          </div>
        </div>

        {/* Collection tabs */}
        <div className="max-w-4xl mx-auto px-4 pb-3 flex gap-2">
          {collections.map((c, i) => (
            <button
              key={c.slug}
              onClick={() => { setActiveTab(i); window.scrollTo(0, 0); }}
              style={{
                fontFamily: "var(--font-body)",
                fontSize: 14,
                fontWeight: 600,
                padding: "6px 16px",
                borderRadius: 20,
                border: "none",
                cursor: "pointer",
                transition: "all 0.15s",
                backgroundColor: i === activeTab ? "#111827" : "#f3f4f6",
                color: i === activeTab ? "white" : "#6b7280",
              }}
            >
              {c.name}
            </button>
          ))}
        </div>
      </header>

      {/* Main */}
      <main className="max-w-4xl mx-auto px-4 py-6 space-y-8">
        {col.categories.map((cat) => {
          const items = col.items.filter((i) => i.category === cat.name);
          if (items.length === 0) return null;
          return (
            <section key={cat.name}>
              <h2 style={{ fontFamily: "var(--font-heading)" }} className="text-base font-bold text-gray-800 px-1 mb-4">
                {cat.emoji} {cat.name}
              </h2>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                  gap: 10,
                }}
              >
                {items.map((item) => (
                  <ItemCard key={item.id} item={item} onClick={() => navigate(col, item)} />
                ))}
              </div>
            </section>
          );
        })}

        {/* Footer */}
        <footer className="pt-6 pb-10 text-center">
          <p className="text-sm text-gray-400" style={{ fontFamily: "var(--font-body)" }}>
            stories.levifikri.com
          </p>
        </footer>
      </main>
    </div>
  );
}

export default App;
